import { useEffect, useState } from "react";
import { CONFIG } from "../config";
import defaultLogo from "../assets/WebTwin.png";

const CACHE_KEY = "navbar_logo";

/** @returns {string | null | undefined} path from cache; `undefined` = never cached */
export function readCachedNavbarLogoPath() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw == null) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "path" in parsed) {
      return parsed.path ?? null;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Persist backend relative path (`null` = use default logo). */
export function writeCachedNavbarLogoPath(path) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ path: path ?? null }));
}

export function buildNavbarLogoUrl(path, fallback = defaultLogo) {
  if (!path) return fallback;
  const base = String(CONFIG.BASE_URI || "").replace(/\/$/, "");
  const relative = String(path).startsWith("/") ? path : `/${path}`;
  return `${base}${relative}`;
}

/**
 * Navbar logo with localStorage cache.
 * - First visit: `null` while settings load, then resolves and caches the path.
 * - Later visits: shows cached URL immediately; only replaces when backend path changes.
 */
export function useNavbarLogo(navbarLogoPath, isLoading, fallback = defaultLogo) {
  const [logoSrc, setLogoSrc] = useState(() => {
    const cachedPath = readCachedNavbarLogoPath();
    if (cachedPath === undefined) return null;
    return buildNavbarLogoUrl(cachedPath, fallback);
  });

  useEffect(() => {
    if (isLoading) return;

    const nextPath = navbarLogoPath ?? null;
    const cachedPath = readCachedNavbarLogoPath();

    if (cachedPath === undefined || cachedPath !== nextPath) {
      writeCachedNavbarLogoPath(nextPath);
    }

    setLogoSrc(buildNavbarLogoUrl(nextPath, fallback));
  }, [isLoading, navbarLogoPath, fallback]);

  return logoSrc;
}
